import apiService from "services/api";
import { CustomAlert, CustomModal } from "components";
import {
  studentLessonRecordTableActions,
  studentLessonRecordTableColumns,
} from "./constants";
import {
  initialStudentRecordTableModalState,
  studentRecordTableModalReducer,
  studentRecordTableModalReducerActions,
  modalAction,
  modalInitialState,
  modalReducer,
} from "./reducers";
import { tabs } from "./LessonRecordTabs";
import { TabContent, Nav, NavItem, NavLink, Button } from "reactstrap";
import { useReducer, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNotificationContext } from "contexts";
import { imageTypes } from "./constants";
import { useThrottle } from "hooks/useThrottle";
import { FilterableTable } from "components/FilterableTable";

const PendingStudentRecords = () => {
  const { createNotification } = useNotificationContext();

  const [confirmationAlertProps, confirmationAlertDispatch] = useReducer(
    modalReducer,
    modalInitialState,
  );
  const [confirmationCount, setConfirmationCount] = useState(0);

  const triggerConfirmation = () => {
    confirmationAlertDispatch({ type: modalAction.OPEN });
  };

  const closeConfirmationAlert = () => {
    setConfirmationCount(0);
    confirmationAlertDispatch({ type: modalAction.CLOSE });
  };

  const confirmSubmit = () => {
    if (confirmationCount <= 2) {
      return setConfirmationCount((count) => count + 1);
    }
    approveStudentRecord(getValues());
  };

  const throttledConfirmSubmit = useThrottle(confirmSubmit, 500);

  const formMethods = useForm({ reValidateMode: "onChange" });
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = formMethods;

  const [studentRecordTableModalState, studentRecordTableModalDispatch] =
    useReducer(
      studentRecordTableModalReducer,
      initialStudentRecordTableModalState,
    );

  const [refetchCheck, setRefetchCheck] = useState(false);

  const [editable, setEditable] = useState(false);

  const { studentRecord } = studentRecordTableModalState;

  const initialTab = tabs[0].index;
  const [tabToShow, setTabToShow] = useState(initialTab);
  const switchTab = (tabNumber) => () =>
    tabNumber !== tabToShow && setTabToShow(tabNumber);

  const showModal = (row) => {
    studentRecordTableModalDispatch({
      type: studentRecordTableModalReducerActions.OPEN,
      payload: row,
    });
    const studentRecord = {
      ...row,
      inClassPhotos: row.lessonFiles.filter(
        (lessonFile) => lessonFile.type === imageTypes.IN_CLASS_IMAGES,
      ),
      classworkPhotos: row.lessonFiles.filter(
        (lessonFile) => lessonFile.type === imageTypes.CLASSWORK,
      ),
    };
    reset(studentRecord);
  };

  const hideModal = () => {
    setTabToShow(initialTab);
    studentRecordTableModalDispatch({
      type: studentRecordTableModalReducerActions.HIDE,
    });
    closeConfirmationAlert();
  };

  const approveStudentRecord = (data) => {
    apiService.lessonRecord
      .approve(
        studentRecordTableModalState.studentRecord.id,
        isDirty
          ? {
              studentReport: data.studentReport,
              lessonFiles: [
                ...(data.inClassPhotos || []),
                ...(data.classworkPhotos || []),
              ],
            }
          : undefined,
      )
      .then((res) => {
        createNotification({
          type: "success",
          message: "Succesfully approve lesson report",
        });
        hideModal();
        setRefetchCheck((prev) => !prev);
      });
  };

  const isAttendanceRequired = (studentRecord) =>
    !studentRecord?.lessonDetail?.teacherAttendanceRecorded &&
    !studentRecord?.studentAttendanceRecorded;

  const approveButtonText = useCallback((studentRecord) => {
    const arrText = [
      isAttendanceRequired(studentRecord) ? "Attendance" : undefined,
      !studentRecord?.studentReportAdded ? "Student Report" : undefined,
    ].filter((check) => check);
    if (!arrText.length) return "Approve";
    return arrText.join(arrText.length > 1 ? " and " : "") + " Required";
  }, []);

  const resetFields = () => reset(studentRecordTableModalState.studentRecord);

  return (
    <div className="content">
      <form onSubmit={handleSubmit(triggerConfirmation)}>
        <CustomAlert
          show={confirmationAlertProps.show}
          hideModal={closeConfirmationAlert}
          title="Confirm Student Report?"
          submitButtonText={
            confirmationCount <= 2
              ? `Confirm (${confirmationCount}/3)`
              : "100% Certain to continue?"
          }
          submitModal={throttledConfirmSubmit}
          width="500px"
          fitToParent={false}
        />

        <CustomModal
          show={studentRecordTableModalState.show}
          hideModal={hideModal}
          buttons={
            <>
              <Button
                type="submit"
                color="success"
                disabled={
                  !studentRecordTableModalState.studentRecord
                    ?.studentReportAdded ||
                  isAttendanceRequired(
                    studentRecordTableModalState.studentRecord,
                  )
                }
              >
                {approveButtonText(studentRecordTableModalState.studentRecord)}
              </Button>
            </>
          }
          triggerAlertWhenClose={isDirty}
          triggerAlertMessageWhenClose="Confirm close? Changes will be discarded"
        >
          <Nav pills className="nav-pills-primary mt-2">
            {tabs.map((tab) => (
              <NavItem key={tab.index} className="mb-2">
                <NavLink
                  className={`rounded px-3 py-1
                                    ${tabToShow === tab.index ? "active" : ""}
                                `}
                  onClick={switchTab(tab.index)}
                  style={{ cursor: "pointer" }}
                >
                  <small>{tab.name}</small>
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={tabToShow} className="mt-4">
            {tabs.map(({ Component }, index) => (
              <Component
                key={index}
                studentRecord={studentRecord}
                editable={editable}
                setEditable={setEditable}
                {...formMethods}
                resetFields={resetFields}
                tabToShow={tabToShow}
                canEdit={true}
              />
            ))}
          </TabContent>
        </CustomModal>
      </form>
      <FilterableTable
        query={(filters) => () =>
          apiService.lessonRecord.studentRecord.get(
            undefined,
            {
              past_records: true,
              student_report_approved: false,
              lesson_date_completed: true,
              lesson_completed: true,
            },
            filters,
          )
        }
        action={(row) =>
          studentLessonRecordTableActions({
            showModal,
            canAdd: false,
            row,
          })
        }
        title="Pending Lesson Records"
        actionPrepend
        queryKey="studentRecords"
        columns={studentLessonRecordTableColumns(
          studentLessonRecordTableActions({
            showModal,
            canAdd: false,
          }),
        )}
        refetchCheck={refetchCheck}
        filters={[
          {
            filterName: "date_from",
            operator: "contains",
            placeholder: "Date",
            type: "date",
            label: "Date From",
          },
          {
            filterName: "date_to",
            operator: "contains",
            placeholder: "Date",
            type: "date",
            label: "Date To",
          },
          { filterName: "centre", operator: "contains", placeholder: "Centre" },
          {
            filterName: "teacher_name",
            operator: "contains",
            placeholder: "Teacher Name",
          },
          {
            filterName: "student_name",
            operator: "contains",
            placeholder: "Student Name",
          },
          {
            filterName: "course_name",
            operator: "contains",
            placeholder: "Subject",
          },
          {
            filterName: "is_classwork_added",
            operator: "contains",
            label: "Classwork?",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
          {
            filterName: "is_in_class_image_added",
            operator: "contains",
            label: "In-Class Image?",
            type: "select",
            options: [
              { value: null, label: "All" },
              { value: true, label: "Yes" },
              { value: false, label: "No" },
            ],
          },
        ]}
      />
    </div>
  );
};

export default PendingStudentRecords;
